// @flow

import type { PropsSchema, JsEnv } from '../../type'

import * as React from 'react'
import { appendPropsSchema, appendPropsActions } from '../util/schemaUtil'
import './animate2.min.css'
import './customAnimation.css'

export default function (WrappedComponent: React.ComponentType<*>) {
  class Animation extends React.PureComponent<{
    style: any,
    animations: Array<any>,
    id: string,
    isReadyDo: boolean,
    jsEnv: JsEnv,
    callActions: (actionName: string, dynamicData: any) => {}
  }> {
    static schema: PropsSchema = appendPropsSchema(WrappedComponent, [
      {
        title: '动画列表',
        type: 'array',
        key: 'animations',
        item: [
          {
            type: 'select',
            key: 'animat',
            title: '动画：',
            options: [
              {
                groupName: '入场类',
                options: [
                  ['fadeInDown', '从上淡入'],
                  ['fadeInLeft', '从左淡入'],
                  ['fadeInRight', '从右淡入'],
                  ['fadeInUp', '从下淡入'],

                  ['bounceInUp', '从上飞入'],
                  ['bounceInLeft', '从左飞入'],
                  ['bounceInRight', '从右飞入'],
                  ['bounceInDown', '从下飞入'],

                  ['slideDown', '从上弹入'],
                  ['slideLeft', '从左弹入'],
                  ['slideRight', '从右弹入'],
                  ['slideUp', '从下弹入'],

                  ['rotateInDownRight', '右上旋入'],
                  ['rotateInUpLeft', '右下旋入'],
                  ['rotateInDownLeft', '左上旋入'],
                  ['rotateInUpRight', '左下旋入'],

                  ['fadeIn', '淡入'],
                  ['bounceIn', '飞入'],
                  ['translate', '正向旋转'],
                  ['translateReverse', '逆向旋转'],

                  ['zoomIn', '从小到大'],
                  ['zoomOut', '从大到小'],
                  ['bounceOut', '弹性放大']
                ]
              },
              {
                groupName: '强调类',
                options: [
                  ['rotate2d', '旋转2D'],
                  ['shake', '抖动'],
                  ['float2', '左右浮动'],
                  ['float', '上下浮动'],
                  ['flash', '闪烁'],
                  ['wobble', '左右摇晃'],
                  ['tada', 'Q弹晃动'],
                  ['clock3d', '钟表晃动'],
                  ['bounces', '上下弹动'],
                  ['microRotate', '左右摆动'],
                  ['levelFlip', '水平翻转'],
                  ['verticalFlip', '垂直翻转'],
                  ['auraIn', '放大旋转'],
                  ['freeFloat', '自由浮动'],
                  ['breathe', '呼吸放大']
                ]
              },
              {
                groupName: '退出类',
                options: [
                  ['fadeOutDown', '从上淡出'],
                  ['fadeOutRight', '从左淡出'],
                  ['fadeOutLeft', '从右淡出'],
                  ['fadeOutUp', '从下淡出'],
                  ['fadeOutDownLeft', '流星上左淡出'],
                  ['fadeOutDownRight', '流星上右淡出'],
                  ['fadeOutUpLeft', '流星下左淡出'],
                  ['fadeOutUpRight', '流星下右淡出'],
                  ['starOutDown', '流星上淡出'],
                  ['starOutUp', '流星下淡出'],
                  ['starOutLeft', '流星左淡出'],
                  ['starOutRight', '流星右淡出'],
                  ['fadeOut', '淡出'],
                  ['barrageLeft', '弹幕向左'],
                  ['barrageRight', '弹幕向右'],
                  ['rainsBottom', '红包雨向下'],
                  ['rainsLeft', '红包雨向左']
                ]
              }
            ],
            defaultValue: 'fadeInDown'
          },
          {
            type: 'number',
            key: 'duration',
            title: '持续(s)',
            width: 100,
            step: 0.01,
            defaultValue: '1'
          },
          {
            type: 'number',
            key: 'delay',
            title: '延迟(s)',
            width: 100,
            step: 0.01
          },
          {
            type: 'number',
            key: 'time',
            title: '次数：',
            width: 100,
            defaultValue: '1'
          },
          {
            type: 'select',
            key: 'timing',
            title: '速度曲线：',
            options: [
              ['linear', '线性'],
              ['ease', 'ease'],
              ['ease-in', '低速开始'],
              ['ease-out', '低速结束'],
              ['ease-in-out', '低速开始和结束']
            ],
            defaultValue: 'ease'
          },
          {
            title: '执行规则',
            key: 'isRunRule',
            type: 'code'
          }
        ]
      },
      {
        type: 'event',
        title: '动画开始',
        event: 'animationStart'
      },
      {
        type: 'event',
        title: '动画结束',
        event: 'animationEnd'
      },
      {
        title: '是否就绪执行',
        type: 'boolean',
        key: 'isReadyDo'
      }
    ]);

    static actions = appendPropsActions(WrappedComponent, [
      {
        title: '执行动画',
        name: 'runAnimate'
      },
      {
        title: '停止动画',
        name: 'stopAnimate'
      }
    ]);

    wrappedComponent: ?React.Component<*>;

    actionRunAnimate = () => {
      setTimeout(() => {
        let { id } = this.props
        let element = document.getElementById(id)
        if (!element) return
        element.style.animation = ''
        let newStyle = this.appendAnimations(true)
        setTimeout(() => {
          if (element) element.style.animation = newStyle.animation
        }, 0)
      }, 0)
    };

    actionStopAnimate = () => {
      let { id } = this.props
      let element = document.getElementById(id)
      if (!element) return
      element.style.animation = ''
    };

    addAnimationEvent = (target: any) => {
      let { animations } = this.props
      if (!animations || animations.length <= 0) return null
      target.addEventListener('webkitAnimationStart', this.animationStart)
      target.addEventListener('webkitAnimationEnd', this.animationEnd)
    };

    animationStart = (e: AnimationEvent) => {
      let { animations, callActions, jsEnv } = this.props
      if (!animations) return
      let firstAnimName = ''
      for (let i = 0; i < animations.length; i++) {
        const item = animations[i]
        const { isRunRule, animat } = item
        if (isRunRule) {
          const isRun = jsEnv.runCode(isRunRule)
          if (!isRun) continue
        }
        firstAnimName = animat
        break
      }
      if (firstAnimName === e.animationName) {
        callActions('animationStart', { animationName: e.animationName })
      }
    };

    animationEnd = (e: AnimationEvent) => {
      let { animations, callActions, jsEnv } = this.props
      if (!animations) return
      let lastAnimName = ''
      for (let i = animations.length - 1; i >= 0; i--) {
        const item = animations[i]
        const { isRunRule, animat } = item
        if (isRunRule) {
          const isRun = jsEnv.runCode(isRunRule)
          if (!isRun) continue
        }
        lastAnimName = animat
        break
      }
      if (lastAnimName === e.animationName) {
        callActions('animationEnd', { animationName: e.animationName })
      }
    };

    render (): React.Node {
      const style = this.appendAnimations(false)
      return (
        <WrappedComponent
          ref={ref => {
            if (ref) {
              this.wrappedComponent = ref
            }
          }}
          {...this.props}
          style={style}
          addAnimationEvent={this.addAnimationEvent}
        />
      )
    }

    appendAnimations = (isActionDo: boolean) => {
      let { animations, style, isReadyDo, jsEnv } = this.props
      if (!animations || (!isReadyDo && !isActionDo)) return style
      if (!style) style = {}
      let styleAnimatoins = []
      for (let i = 0; i < animations.length; i++) {
        const item = animations[i]
        const { isRunRule, animat, duration, delay, time, timing } = item
        if (isRunRule) {
          const isRun = jsEnv.runCode(isRunRule)
          if (!isRun) continue
        }
        styleAnimatoins.push(
          `${animat} ${duration || 0}s ${delay || 0}s ${
            time === '0' ? 'infinite' : time
          } ${timing} ${i === 0 ? 'both' : 'forwards'}`
        )
      }
      let animation = styleAnimatoins.join(',')
      style = Object.assign({}, style, { animation })
      return style
    };
  }

  return Animation
}



// WEBPACK FOOTER //
// ./src/projects/easy/public/Comps/hoc/animation.js